import { memo } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { clsx } from "clsx";

// We don't have @tailwindcss/typography, so map the markdown elements to
// explicitly styled elements following the app's design conventions
// (zinc neutrals, indigo links, semantic text classes).
const components: Components = {
    h1: ({ children }) => (
        <h1 className="mt-8 mb-4 text-2xl font-semibold tracking-tight text-default first:mt-0">
            {children}
        </h1>
    ),
    h2: ({ children }) => (
        <h2 className="mt-8 mb-3 text-xl font-semibold tracking-tight text-default first:mt-0">
            {children}
        </h2>
    ),
    h3: ({ children }) => (
        <h3 className="mt-6 mb-2 text-lg font-semibold tracking-tight text-default">
            {children}
        </h3>
    ),
    h4: ({ children }) => (
        <h4 className="mt-4 mb-2 text-base font-semibold tracking-tight text-default">
            {children}
        </h4>
    ),
    p: ({ children }) => (
        <p className="my-3 text-sm/6 text-default">{children}</p>
    ),
    a: ({ children, href }) => (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-indigo-600 underline hover:text-indigo-500 dark:text-indigo-400"
        >
            {children}
        </a>
    ),
    ul: ({ children }) => (
        <ul className="my-3 ml-6 list-disc space-y-1 text-sm/6 text-default">
            {children}
        </ul>
    ),
    ol: ({ children }) => (
        <ol className="my-3 ml-6 list-decimal space-y-1 text-sm/6 text-default">
            {children}
        </ol>
    ),
    li: ({ children }) => <li className="pl-1">{children}</li>,
    strong: ({ children }) => (
        <strong className="font-semibold text-default">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    // Never an <img>: the browser fetches an image the moment it renders, so a
    // Url in the text could carry data out with nobody clicking anything, and
    // the Csp allows any https image. Chat answers are the reason -- a campaign
    // name can carry an injected instruction to put contact emails in an image
    // Url. Nothing rendered here needs an image, so the alt text stands in.
    img: ({ alt }) => <span>{alt}</span>,
    blockquote: ({ children }) => (
        <blockquote className="my-4 border-l-2 border-zinc-200 pl-4 text-sm/6 text-subtle dark:border-white/10">
            {children}
        </blockquote>
    ),
    hr: () => <hr className="my-6 border-zinc-950/5 dark:border-white/10" />,
    code: ({ children }) => (
        <code className="rounded-md bg-zinc-100 px-1 py-0.5 font-mono text-[0.8em] text-default dark:bg-white/10">
            {children}
        </code>
    ),
    table: ({ children }) => (
        <div className="my-4 overflow-x-auto">
            <table className="w-full text-left text-sm/6 text-default">
                {children}
            </table>
        </div>
    ),
    th: ({ children }) => (
        <th className="border-b border-zinc-950/10 px-3 py-2 font-semibold dark:border-white/10">
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td className="border-b border-zinc-950/5 px-3 py-2 dark:border-white/5">
            {children}
        </td>
    ),
};

function Markdown({
    children,
    className,
}: {
    children: string;
    className?: string;
}) {
    return (
        <div className={clsx(className)}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {children}
            </ReactMarkdown>
        </div>
    );
}

// Memoized because parsing is the expensive part: react-markdown + remark-gfm
// re-parse the whole document on every render. Both props are strings, so the
// default shallow compare is a value compare, and the component reads nothing
// but its props -- chat renders one of these per settled answer, and without
// this each streamed token of a new answer re-parsed every previous one.
export default memo(Markdown);
